import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { WORKFLOW_CHAIN, statusLabel } from '../orders/workflowConstants';
import { formatInr } from '../dashboard/dashboardPeriodUtils';

const STATUS_COLORS = {
  DRAFT: '#64748b',
  APPROVED: '#3b82f6',
  PLACE_ORDER: '#eab308',
  IN_PRODUCTION: '#14b8a6',
  PRODUCTION_DONE: '#06b6d4',
  BUTTA_CUTTING: '#a16207',
  MILL_FINISH: '#f97316',
  BORDER_CUTTING: '#ec4899',
  HANDWORK: '#78716c',
  READY_FOR_DISPATCH: '#22c55e',
  IN_STOCK: '#0ea5e9',
  DISPATCHED: '#10b981',
  DELIVERED: '#7c3aed',
  CANCELLED: '#ef4444',
};

const PIE_COLORS = ['#3b82f6', '#14b8a6', '#22c55e', '#7c3aed', '#f97316', '#ec4899', '#0ea5e9', '#64748b'];

function RevenueTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-sm">
      <p className="font-semibold text-slate-900">{row.label}</p>
      <p className="text-emerald-700 font-semibold mt-1">{formatInr(row.revenue)}</p>
      <p className="text-xs text-slate-500 mt-1">{row.orderCount} orders · {Number(row.pieces || 0).toLocaleString()} pcs</p>
    </div>
  );
}

function StatusRevenueTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-sm">
      <p className="font-semibold text-slate-900">{statusLabel(row.status)}</p>
      <p className="text-emerald-700 font-semibold mt-1">{formatInr(row.revenue)}</p>
      <p className="text-xs text-slate-500 mt-1">{row.orderCount} orders</p>
    </div>
  );
}

export function SalesRankBarChart({ title, rows, loading, emptyLabel = 'No data in this period' }) {
  const data = useMemo(
    () => (rows || [])
      .filter((r) => Number(r.revenue) > 0 || Number(r.orderCount) > 0)
      .map((r) => ({
        label: r.label || '—',
        revenue: Number(r.revenue) || 0,
        orderCount: Number(r.orderCount) || 0,
        pieces: Number(r.pieces) || 0,
      })),
    [rows]
  );

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">{title}</h3>
        <div className="h-56 rounded-xl bg-slate-100 animate-pulse" />
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">{title}</h3>
        <div className="h-40 flex items-center justify-center text-sm text-slate-500">{emptyLabel}</div>
      </div>
    );
  }

  const chartHeight = Math.max(220, data.length * 36);

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">{title}</h3>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
          <XAxis type="number" tickFormatter={(v) => formatInr(v)} tick={{ fontSize: 10, fill: '#64748b' }} />
          <YAxis
            type="category"
            dataKey="label"
            width={100}
            tick={{ fontSize: 11, fill: '#475569' }}
            interval={0}
          />
          <Tooltip content={<RevenueTooltip />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
          <Bar dataKey="revenue" radius={[0, 4, 4, 0]} maxBarSize={22}>
            {data.map((entry, index) => (
              <Cell key={entry.label} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function SalesRevenueByStatusChart({ rows, loading }) {
  const data = useMemo(() => {
    const known = new Set(WORKFLOW_CHAIN.map((w) => w.key));
    return (rows || [])
      .filter((r) => known.has(r.status) && (Number(r.revenue) > 0 || Number(r.orderCount) > 0))
      .map((r) => ({
        status: r.status,
        name: statusLabel(r.status),
        revenue: Number(r.revenue) || 0,
        orderCount: Number(r.orderCount) || 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [rows]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Revenue by order stage</h3>
        <div className="h-64 rounded-xl bg-slate-100 animate-pulse" />
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Revenue by order stage</h3>
        <div className="h-48 flex items-center justify-center text-sm text-slate-500">No revenue in this period</div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900 mb-1">Revenue by order stage</h3>
      <p className="text-xs text-slate-500 mb-3">Where order value sits in the workflow</p>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={data}
            dataKey="revenue"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={52}
            outerRadius={96}
            paddingAngle={2}
          >
            {data.map((entry) => (
              <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || '#94a3b8'} />
            ))}
          </Pie>
          <Tooltip content={<StatusRevenueTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center mt-2 max-h-24 overflow-y-auto">
        {data.map((d) => (
          <span key={d.status} className="inline-flex items-center gap-1.5 text-[10px] text-slate-600">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: STATUS_COLORS[d.status] || '#94a3b8' }}
              aria-hidden
            />
            {d.name}
          </span>
        ))}
      </div>
    </div>
  );
}

export function SalesTopDesignsTable({ rows, loading }) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Top designs by revenue</h3>
        <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />
      </div>
    );
  }

  const data = (rows || []).filter((r) => Number(r.revenue) > 0 || Number(r.orderCount) > 0);

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm overflow-hidden">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">Top designs by revenue</h3>
      {!data.length ? (
        <p className="text-sm text-slate-500 py-8 text-center">No design sales in this period</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <th className="py-2 pr-3 font-semibold">Sample #</th>
                <th className="py-2 pr-3 font-semibold text-right">Orders</th>
                <th className="py-2 pr-3 font-semibold text-right">Pieces</th>
                <th className="py-2 font-semibold text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.label} className="border-b border-slate-50 last:border-0">
                  <td className="py-2.5 pr-3 font-mono font-medium text-slate-900">{row.label}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">{row.orderCount}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">{Number(row.pieces || 0).toLocaleString()}</td>
                  <td className="py-2.5 text-right tabular-nums font-semibold text-emerald-700">{formatInr(row.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
